export const MDS_COUNTRY_CODE = {
  BAHRAIN: "BHR",
  INDIA: "IND",
  KUWAIT: "KWT",
  QATAR: "QAT",
  OMAN: "OMN",
  SAUDI_ARABIA: "SAU",
  UAE: "ARE",
  USA: "USA",
};

export const OMS_REGIONS = {
  INDIA: "india",
  US: "us",
  GCC: "gcc",
};

export const INDIA_COUNTRY_ID = 1;
export const USA_COUNTRY_ID = 8;
export const GCC_COUNTRY_IDS = [2, 3, 4, 5, 6, 7];

/** Stable ISD → region map for phone-number search. */
export const OMS_REGION_BY_ISD_CODE = {
  "+91": OMS_REGIONS.INDIA,
  "+1": OMS_REGIONS.US,
  // "+971": OMS_REGIONS.GCC,
  // "+966": OMS_REGIONS.GCC,
  // "+974": OMS_REGIONS.GCC,
  // "+968": OMS_REGIONS.GCC,
  // "+965": OMS_REGIONS.GCC,
  // "+973": OMS_REGIONS.GCC,
};

/** Flag emoji per ISD (aligned with Plutus order-punching-dashboard). */
export const ISD_FLAG_BY_CODE = {
  "+91": "🇮🇳",
  "+1": "🇺🇸",
};

/** ISD codes shown in search-user ISD dropdown. GCC hidden for now. */
export const ISD_DROPDOWN_VISIBLE_CODES = [
  "+91",
  "+1",
  // "+971",
  // "+966",
  // "+974",
  // "+968",
  // "+965",
  // "+973",
];

export const isIsdVisibleInDropdown = (isdCode) =>
  ISD_DROPDOWN_VISIBLE_CODES.includes(String(isdCode ?? "").trim());

/** Country code → region map for UAM/country dropdown. */
export const OMS_REGION_BY_COUNTRY_CODE = {
  [MDS_COUNTRY_CODE.INDIA]: OMS_REGIONS.INDIA,
  [MDS_COUNTRY_CODE.USA]: OMS_REGIONS.US,
  [MDS_COUNTRY_CODE.BAHRAIN]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.KUWAIT]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.QATAR]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.OMAN]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.SAUDI_ARABIA]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.UAE]: OMS_REGIONS.GCC,
};

const OMS_REGION_BY_COUNTRY_ID = {
  [INDIA_COUNTRY_ID]: OMS_REGIONS.INDIA,
  [USA_COUNTRY_ID]: OMS_REGIONS.US,
  ...Object.fromEntries(
    GCC_COUNTRY_IDS.map((countryId) => [countryId, OMS_REGIONS.GCC]),
  ),
};

const trimBaseUrl = (url) => {
  if (url == null) {
    return "";
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\/+$/, "");
};

export const OMS_BASE_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(process.env.REACT_APP_OMS_BASE_URL),
  [OMS_REGIONS.US]: trimBaseUrl(process.env.REACT_APP_OMS_US_BASE_URL),
  [OMS_REGIONS.GCC]: trimBaseUrl(process.env.REACT_APP_OMS_GCC_BASE_URL),
};

/** Gateway base host per region (unified search-user path appended by callers). */
export const GATEWAY_LEARN_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(process.env.REACT_APP_GATEWAY_LEARN_URL),
  [OMS_REGIONS.US]: trimBaseUrl(process.env.REACT_APP_GATEWAY_LEARN_US_URL),
  [OMS_REGIONS.GCC]: trimBaseUrl(process.env.REACT_APP_GATEWAY_LEARN_GCC_URL),
};

export const resolveOmsRegion = ({
  countryId,
  countryCode,
  isdCode,
} = {}) => {
  if (isdCode) {
    const regionFromIsd = OMS_REGION_BY_ISD_CODE[String(isdCode).trim()];
    if (regionFromIsd) {
      return regionFromIsd;
    }
  }

  if (countryCode) {
    const regionFromCode =
      OMS_REGION_BY_COUNTRY_CODE[String(countryCode).trim().toUpperCase()];
    if (regionFromCode) {
      return regionFromCode;
    }
  }

  if (countryId != null && countryId !== "") {
    const regionFromId = OMS_REGION_BY_COUNTRY_ID[Number(countryId)];
    if (regionFromId) {
      return regionFromId;
    }
  }

  return OMS_REGIONS.INDIA;
};

export const isLearnOnlyRegion = (options = {}) => {
  const region = resolveOmsRegion(options);
  return region === OMS_REGIONS.US || region === OMS_REGIONS.GCC;
};

/**
 * Live country context from the current URL.
 * Prefer this over session/local storage so a refresh always re-reads countryId.
 */
export const getCountryIdFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search).get("countryId");
};

export const getIsdCodeFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search).get("isdCode");
};

const resolveRegionFromOptions = (options = {}) => {
  const countryId =
    options.countryId != null && options.countryId !== ""
      ? options.countryId
      : getCountryIdFromUrl();
  const isdCode =
    options.isdCode != null && options.isdCode !== ""
      ? options.isdCode
      : getIsdCodeFromUrl();

  return resolveOmsRegion({
    countryId,
    countryCode: options.countryCode,
    isdCode,
  });
};

const getRegionalUrl = ({
  options = {},
  urlByRegion,
  envKeyByRegion,
  serviceLabel,
}) => {
  const region = resolveRegionFromOptions(options);
  const baseUrl = urlByRegion[region];

  if (!baseUrl) {
    throw new Error(
      `${serviceLabel} URL is not configured for region "${region}". Set ${envKeyByRegion[region]}.`,
    );
  }

  return baseUrl;
};

export const getOneOmsBaseUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: OMS_BASE_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_OMS_BASE_URL",
      [OMS_REGIONS.US]: "REACT_APP_OMS_US_BASE_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_OMS_GCC_BASE_URL",
    },
    serviceLabel: "ONE OMS",
  });

export const getGatewayLearnBaseUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: GATEWAY_LEARN_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_GATEWAY_LEARN_URL",
      [OMS_REGIONS.US]: "REACT_APP_GATEWAY_LEARN_US_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_GATEWAY_LEARN_GCC_URL",
    },
    serviceLabel: "Gateway learn",
  });

export const getSearchUserUrl = (options = {}) =>
  `${getGatewayLearnBaseUrl(options)}/api/v1/unified/search-user`;
